import { Injectable, computed, signal } from '@angular/core';
import type { DocLoadingConfig, DocLoadingResult, DocLoadingState } from './sr-doc-loading.types';

/**
 * Servicio singleton que controla el modal `<sr-doc-loading>` global durante
 * operaciones largas (emisión DTE, generación PDF, facturación EP, etc.).
 *
 * **Diseño: progress indeterminate + mensaje rotativo.**
 *
 * Antes este componente avanzaba pasos con un timer interno (`setInterval`)
 * pintando una barra segmentada que daba la impresión de "progreso real".
 * Eso era engañoso: el backend NO reporta progreso, así que la barra avanzaba
 * 1→5 a velocidad fija independiente del estado real, llegando a "Casi
 * listo…" y quedándose esperando. Si la operación tardaba más de N segundos,
 * el usuario veía el progreso completo pero la respuesta no llegaba — eso
 * era la "Opción C" rechazada por el sprint.
 *
 * Ahora la barra es SIEMPRE indeterminada (animación CSS sin porcentaje), y
 * el TEXTO debajo rota cada `stepDurationMs` (default 2.5s) mostrando los
 * mensajes contextuales del `config.steps`. El usuario ve "Validando…",
 * después "Emitiendo DTE…", etc. — sabe en qué fase aproximada está, sin
 * la falsa promesa de un % que termina y se queda colgado.
 *
 * Cuando el backend responde, el caller llama a `complete(result)` y el
 * modal muestra la pantalla OK con folio + pills.
 *
 * Uso típico:
 * ```ts
 * this.docLoading.start({ steps: [...], stamp: true });
 * this.api.emitir(...).subscribe({
 *   next: (res) => this.docLoading.complete({ title: '...', pills: [...] }),
 *   error: () => this.docLoading.close(),
 * });
 * ```
 */
@Injectable({ providedIn: 'root' })
export class SrDocLoadingService {
  private readonly _state = signal<DocLoadingState>({
    visible: false,
    config: null,
    currentStep: 0,
    done: false,
    result: null,
  });

  /** Estado observable (read-only). El componente lo lee para renderizar. */
  readonly state = this._state.asReadonly();

  /** Conveniencia: total de pasos del scenario actual. */
  readonly totalSteps = computed(() => this._state().config?.steps.length ?? 0);

  private timer: ReturnType<typeof setInterval> | null = null;

  /**
   * Abre el modal con progress INDETERMINATE. Si hay múltiples mensajes en
   * `config.steps`, el texto rota cada `stepDurationMs` (default 2500ms).
   * La barra de progreso siempre es indeterminada (animación CSS sin %),
   * jamás avanza fingiendo progreso del backend.
   *
   * Si hay 1 solo paso o `config.indet=true`, no rota — texto fijo.
   */
  start(config: DocLoadingConfig): void {
    this.stopTimer();
    this._state.set({
      visible: true,
      config,
      currentStep: 0,
      done: false,
      result: null,
    });

    // 1 solo paso → mensaje fijo, sin rotación.
    if (config.steps.length <= 1) return;

    // Más de 1 paso → rotar texto cada stepDurationMs sin avanzar barra.
    // Default subido de 1150ms a 2500ms — el ritmo anterior daba sensación
    // de "Opción C avanzando rápido". 2.5s es ritmo de lectura cómoda.
    const duration = config.stepDurationMs ?? 2500;
    this.timer = setInterval(() => {
      const s = this._state();
      if (!s.config) return;
      const next = s.currentStep + 1;
      // Frenar en el penúltimo paso — el último ("Casi listo, finalizando…")
      // se mantiene hasta que el caller llame a complete(). Si la operación
      // tarda demasiado, el último mensaje queda visible (no avanza más).
      if (next >= s.config.steps.length) {
        this.stopTimer();
        this._state.set({ ...s, currentStep: s.config.steps.length - 1 });
        return;
      }
      this._state.set({ ...s, currentStep: next });
    }, duration);
  }

  /**
   * Marca el proceso como completo y muestra la pantalla OK con el resultado.
   * El caller debe llamar a `close()` para cerrarla cuando navegue al detalle
   * o el usuario haga click en el CTA.
   */
  complete(result: DocLoadingResult): void {
    this.stopTimer();
    const s = this._state();
    this._state.set({ ...s, done: true, result });
  }

  /** Cierra el modal y resetea el state. */
  close(): void {
    this.stopTimer();
    this._state.set({
      visible: false,
      config: null,
      currentStep: 0,
      done: false,
      result: null,
    });
  }

  private stopTimer(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }
}
